
import React, { useState } from 'react';
import { 
  ShieldCheck, 
  Globe, 
  Zap, 
  MapPin, 
  Mail, 
  Phone, 
  ArrowRight, 
  Lock, 
  ChevronRight,
  Database,
  Search,
  Activity,
  UserCheck,
  Building2,
  Cpu,
  Moon,
  Sun,
  Star,
  Quote,
  Shield,
  ShieldAlert,
  Layers,
  Terminal,
  Scale,
  Globe2,
  FileSearch,
  UserPlus,
  Users,
  Radar,
  Wallet,
  Leaf,
  BarChart3,
  Ghost,
  Fingerprint,
  FileCheck,
  TrendingUp,
  Workflow,
  CreditCard,
  Smartphone,
  MessageSquare,
  Headphones,
  CheckCircle2,
  Send,
  Compass,
  Award,
  AlertTriangle,
  History,
  Briefcase,
  Map
} from 'lucide-react';
import { AuthState } from '../types';
import { Language, translations } from '../translations';

interface LandingPageProps {
  setAuthState: (state: AuthState) => void;
  darkMode: boolean;
  setDarkMode: (val: boolean) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ setAuthState, darkMode, setDarkMode, language, setLanguage }) => {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const t = translations[language];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleContactSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const formData = new FormData(e.currentTarget);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch("https://formsubmit.co/ajax/hello@minervaregtech.com", {
        method: "POST",
        headers: { 
            'Content-Type': 'application/json',
            'Accept': 'application/json'
        },
        body: JSON.stringify(data)
      });

      if (response.ok) {
        setSubmitted(true);
      } else {
        throw new Error("Failed to send message");
      }
    } catch (err) {
      setError("Transmission failed. Please try again or contact hello@minervaregtech.com directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-slate-950 text-slate-900 dark:text-slate-100 font-sans selection:bg-blue-100 selection:text-blue-600 transition-colors duration-500 scroll-smooth">
      {/* Navigation */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 dark:bg-slate-950/80 backdrop-blur-xl border-b border-slate-100 dark:border-slate-900">
        <div className="max-w-7xl mx-auto px-6 h-20 flex items-center justify-between">
          <div className="flex items-center space-x-3 group cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
            <img src="assets/logo.png" alt="Minerva Logo" className="w-10 h-10 object-contain group-hover:scale-110 transition-transform" />
            <span className="text-xl font-black tracking-tighter uppercase italic dark:text-white">Minerva Regtech</span>
          </div>
          
          <div className="hidden md:flex items-center space-x-10">
            <button onClick={() => setAuthState('about')} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{t.nav.mission}</button>
            <button onClick={() => setAuthState('use-cases')} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{t.nav.useCases}</button>
            <button onClick={() => scrollToSection('services')} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{t.nav.intelligence}</button>
            <button onClick={() => scrollToSection('verification')} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{t.nav.verification}</button>
            <button onClick={() => scrollToSection('reach')} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{t.nav.nodes}</button>
            <button onClick={() => scrollToSection('contact')} className="text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">{t.nav.contact}</button>
          </div>

          <div className="flex items-center space-x-4">
            <div className="flex items-center bg-slate-100 dark:bg-slate-900 p-1 rounded-lg mr-2">
              <Map size={14} className="text-slate-400 mx-2" />
              <button onClick={() => setLanguage('en')} className={`px-2 py-0.5 rounded text-[10px] font-black uppercase transition-all ${language === 'en' ? 'bg-white dark:bg-slate-800 shadow-sm text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}>EN</button>
              <button onClick={() => setLanguage('fr')} className={`px-2 py-0.5 rounded text-[10px] font-black uppercase transition-all ${language === 'fr' ? 'bg-white dark:bg-slate-800 shadow-sm text-blue-600 dark:text-blue-400' : 'text-slate-500 dark:text-slate-400'}`}>FR</button>
            </div>
            <button 
              onClick={() => setDarkMode(!darkMode)}
              className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-900 text-slate-500 dark:text-slate-400 hover:text-blue-600 transition-all border border-transparent dark:border-slate-800"
            >
              {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <button 
              onClick={() => setAuthState('demo')}
              className="text-xs font-black uppercase tracking-widest text-slate-700 dark:text-slate-300 hover:text-blue-600 px-4 py-2 transition-all"
            >
              {t.nav.signIn}
            </button>
            <button 
              onClick={() => setAuthState('demo')}
              className="bg-slate-900 dark:bg-blue-600 text-white px-6 py-2.5 rounded-xl text-xs font-black uppercase tracking-widest hover:bg-slate-800 dark:hover:bg-blue-700 transition-all shadow-xl shadow-slate-200 dark:shadow-none active:scale-95"
            >
              {t.nav.getStarted}
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-48 pb-32 px-6 relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
          <div className="relative z-10">
            <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase mb-8 border border-blue-100 dark:border-blue-800/50 animate-in fade-in slide-in-from-bottom-2 duration-700">
              <Terminal size={12} />
              <span>{t.hero.tagline}</span>
            </div>
            <h1 className="text-6xl md:text-8xl font-black text-slate-900 dark:text-white leading-[0.9] tracking-tighter mb-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
              {t.hero.title1} <br /> <span className="text-blue-600 italic">{t.hero.title2}</span>
            </h1>
            <p className="text-xl text-slate-500 dark:text-slate-400 font-medium leading-relaxed max-w-lg mb-12 animate-in fade-in slide-in-from-bottom-6 duration-700">
              {t.hero.desc}
            </p>
            <div className="flex flex-col sm:flex-row items-center gap-4 animate-in fade-in slide-in-from-bottom-8 duration-700">
              <button 
                onClick={() => setAuthState('demo')}
                className="w-full sm:w-auto bg-blue-600 text-white px-10 py-5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] hover:bg-blue-700 transition-all shadow-2xl shadow-blue-500/20 flex items-center justify-center space-x-3 active:scale-95"
              >
                <span>{t.hero.init}</span>
                <ArrowRight size={16} />
              </button>
              <button 
                onClick={() => scrollToSection('contact')}
                className="w-full sm:w-auto bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 px-10 py-5 rounded-2xl font-black uppercase text-xs tracking-[0.2em] hover:bg-slate-50 dark:hover:bg-slate-800 transition-all flex items-center justify-center shadow-sm"
              >
                {t.hero.contactHub}
              </button>
            </div>
          </div>
          
          <div className="relative animate-in fade-in zoom-in-95 duration-1000 hidden lg:flex items-center justify-center">
            <Globe3D />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[140%] h-[140%] bg-blue-600/5 dark:bg-blue-900/10 rounded-full blur-[160px] -z-10"></div>
          </div>
        </div>
      </section>

      {/* Narrative Section: Full Platform Identity */}
      <section className="py-24 px-6 bg-slate-900 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
            <div className="lg:col-span-8">
              <h2 className="text-3xl md:text-5xl font-black mb-8 leading-tight tracking-tight italic">
                {t.narrative.title}<br /> <span className="text-blue-500">{t.narrative.subtitle}</span>
              </h2>
              <p className="text-xl md:text-2xl text-slate-400 font-medium leading-relaxed mb-10">
                {t.narrative.desc}
              </p>
            </div>
            <div className="lg:col-span-4 grid grid-cols-1 gap-6">
              <div className="p-8 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 transition-all group">
                <ShieldCheck className="text-blue-500 mb-4 group-hover:scale-110 transition-transform" size={32} />
                <h4 className="text-sm font-black uppercase tracking-widest mb-2">{t.narrative.unifiedStack}</h4>
                <p className="text-xs text-slate-500 leading-relaxed">{t.narrative.unifiedStackDesc}</p>
              </div>
              <div className="p-8 bg-white/5 rounded-3xl border border-white/10 hover:bg-white/10 transition-all group">
                <Workflow className="text-emerald-500 mb-4 group-hover:scale-110 transition-transform" size={32} />
                <h4 className="text-sm font-black uppercase tracking-widest mb-2">{t.narrative.instantScale}</h4>
                <p className="text-xs text-slate-500 leading-relaxed">{t.narrative.instantScaleDesc}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-96 h-96 bg-blue-600 rounded-full blur-[140px] opacity-10 translate-x-1/3 -translate-y-1/3"></div>
      </section>

      {/* Portfolio Section: Trusted Partners */}
      <PortfolioSection language={language} />

      {/* Section: Why RegTech Matters */}
      <section id="why-regtech" className="py-32 px-6 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-24 gap-10">
            <div className="max-w-xl">
              <div className="inline-flex items-center space-x-2 px-3 py-1 bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 rounded-full text-[10px] font-black uppercase mb-6 border border-amber-100 dark:border-amber-800/50">
                <AlertTriangle size={12} />
                <span>{t.risk.tagline}</span>
              </div>
              <h2 className="text-5xl md:text-7xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none">{t.risk.title}</h2>
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-lg max-sm max-w-sm leading-relaxed mb-2">
              {t.risk.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <RiskReason icon={<Globe2 size={24}/>} title={t.risk.kyc} desc={t.risk.kycDesc} />
            <RiskReason icon={<Users size={24}/>} title={t.risk.ubo} desc={t.risk.uboDesc} />
            <RiskReason icon={<Scale size={24}/>} title={t.risk.sanctions} desc={t.risk.sanctionsDesc} />
            <RiskReason icon={<Wallet size={24}/>} title={t.risk.crypto} desc={t.risk.cryptoDesc} />
            <RiskReason icon={<Leaf size={24}/>} title={t.risk.esg} desc={t.risk.esgDesc} />
            <RiskReason icon={<ShieldAlert size={24}/>} title={t.risk.fraud} desc={t.risk.fraudDesc} />
            <RiskReason icon={<FileSearch size={24}/>} title={t.risk.audit} desc={t.risk.auditDesc} />
            <RiskReason icon={<TrendingUp size={24}/>} title={t.risk.sentiment} desc={t.risk.sentimentDesc} />
          </div>
        </div>
      </section>

      {/* About Excerpt Section: The Protocol */}
      <section id="about-snippet" className="py-32 px-6 bg-slate-950 text-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-24 items-center">
          <div className="space-y-10 order-2 lg:order-1">
             <div className="space-y-4">
                <h2 className="text-xs font-black text-blue-500 uppercase tracking-[0.4em] flex items-center">
                  <Compass size={16} className="mr-3" /> {t.about.protocol}
                </h2>
                <h3 className="text-5xl md:text-6xl font-black tracking-tighter italic leading-none">{t.about.title}</h3>
             </div>
             <p className="text-xl text-slate-400 leading-relaxed font-medium">
               {t.about.desc1}
             </p>
             <p className="text-lg text-slate-500 leading-relaxed">
               {t.about.desc2}
             </p>
             <button 
               onClick={() => setAuthState('about')}
               className="flex items-center space-x-3 bg-white text-slate-900 px-10 py-5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-600 hover:text-white transition-all shadow-xl active:scale-95 group"
             >
               <span>{t.about.cta}</span>
               <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
             </button>
          </div>
          <div className="relative order-1 lg:order-2">
             <div className="aspect-square bg-white/5 rounded-[4rem] border border-white/10 flex items-center justify-center relative overflow-hidden group">
                <ShieldCheck size={180} className="text-blue-500/20 group-hover:scale-110 transition-transform duration-700" />
                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/5 to-transparent"></div>
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex flex-col items-center">
                   <div className="w-24 h-24 bg-blue-600 rounded-3xl flex items-center justify-center shadow-2xl shadow-blue-500/40 mb-4 animate-float-slow">
                      <Globe size={48} className="text-white" />
                   </div>
                   <span className="text-[10px] font-black uppercase tracking-[0.3em] text-blue-400">{t.about.partner}</span>
                </div>
             </div>
             <div className="absolute -bottom-10 -left-10 w-48 h-48 bg-blue-600 rounded-full blur-[100px] opacity-10"></div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-[50%] h-[100%] bg-blue-600/5 blur-[120px] rounded-full translate-x-1/3 -translate-y-1/3"></div>
      </section>

      {/* Service Grid Section */}
      <section id="services" className="py-32 px-6 bg-slate-50 dark:bg-slate-900/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-24">
            <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none">{t.services.title}</h2>
            <p className="text-slate-500 dark:text-slate-400 font-medium mt-6 text-lg max-w-2xl mx-auto">
              {t.services.desc}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <ServiceCard 
              icon={<Fingerprint className="text-blue-500" />} 
              title={t.services.ip} 
              desc={t.services.ipDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<Building2 className="text-indigo-500" />} 
              title={t.services.kyc} 
              desc={t.services.kycDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<ShieldCheck className="text-emerald-500" />} 
              title={t.services.aml} 
              desc={t.services.amlDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<FileCheck className="text-amber-500" />} 
              title={t.services.lei} 
              desc={t.services.leiDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<Wallet className="text-violet-500" />} 
              title={t.services.blockchain} 
              desc={t.services.blockchainDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<Leaf className="text-emerald-600" />} 
              title={t.services.esg} 
              desc={t.services.esgDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<BarChart3 className="text-blue-400" />} 
              title={t.services.market} 
              desc={t.services.marketDesc} 
              exploreText={t.services.explore}
            />
            <ServiceCard 
              icon={<Ghost className="text-rose-500" />} 
              title={t.services.darkweb} 
              desc={t.services.darkwebDesc} 
              exploreText={t.services.explore}
            />
          </div>
        </div>
      </section>

      {/* Use Cases Preview */}
      <section className="py-32 px-6 bg-slate-900 dark:bg-slate-950 text-white overflow-hidden relative">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div>
              <h2 className="text-xs font-black text-blue-400 uppercase tracking-[0.4em] mb-6">{t.useCases.tagline}</h2>
              <h3 className="text-5xl md:text-6xl font-black italic tracking-tighter leading-none mb-10">{t.useCases.title}</h3>
              <p className="text-slate-400 text-xl font-medium leading-relaxed mb-12">
                {t.useCases.desc}
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-12">
                <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <Building2 className="text-blue-400" size={24} />
                  <span className="font-bold tracking-tight">{t.useCases.banking}</span>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <Scale className="text-emerald-400" size={24} />
                  <span className="font-bold tracking-tight">{t.useCases.regulators}</span>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <Zap className="text-violet-400" size={24} />
                  <span className="font-bold tracking-tight">{t.useCases.fintech}</span>
                </div>
                <div className="flex items-center space-x-4 p-4 bg-white/5 rounded-2xl border border-white/10">
                  <Briefcase className="text-indigo-400" size={24} />
                  <span className="font-bold tracking-tight">{t.useCases.pe}</span>
                </div>
              </div>
              <button 
                onClick={() => setAuthState('use-cases')}
                className="inline-flex items-center space-x-3 bg-blue-600 text-white px-10 py-5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-blue-700 transition-all shadow-2xl shadow-blue-500/20 group"
              >
                <span>{t.useCases.cta}</span>
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </button>
            </div>
            <div className="relative">
              <div className="aspect-square bg-blue-600/10 rounded-[4rem] border border-white/10 flex items-center justify-center relative overflow-hidden">
                <Globe size={300} className="text-blue-500/20 animate-spin-slow" />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
        <div className="absolute top-0 right-0 w-full h-full opacity-5 pointer-events-none">
          <Database size={800} className="absolute -bottom-40 -right-40" />
        </div>
      </section>

      {/* Verification API Suite */}
      <section id="verification" className="py-32 px-6 bg-white dark:bg-slate-950">
         <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
               <div className="relative order-2 lg:order-1">
                  <div className="bg-white dark:bg-slate-900 p-8 md:p-12 rounded-[3.5rem] shadow-2xl border border-slate-100 dark:border-slate-800 relative z-10">
                     <div className="space-y-8">
                        <VerificationRow icon={<CreditCard className="text-emerald-500" />} title={t.verification.iban} desc={t.verification.ibanDesc} status={t.verification.status} />
                        <VerificationRow icon={<Smartphone className="text-blue-500" />} title={t.verification.phone} desc={t.verification.phoneDesc} status={t.verification.status} />
                        <VerificationRow icon={<Mail className="text-indigo-500" />} title={t.verification.email} desc={t.verification.emailDesc} status={t.verification.status} />
                     </div>
                  </div>
                  <div className="absolute -top-10 -left-10 w-40 h-40 bg-blue-600/10 rounded-full blur-3xl"></div>
               </div>
               
               <div className="order-1 lg:order-2">
                  <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase mb-8 border border-blue-100 dark:border-blue-800/50">
                    <Zap size={12} />
                    <span>{t.verification.tagline}</span>
                  </div>
                  <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none mb-8">{t.verification.title}</h2>
                  <p className="text-slate-500 dark:text-slate-400 text-xl font-medium leading-relaxed mb-10">
                    {t.verification.desc}
                  </p>
                  <button 
                    onClick={() => setAuthState('demo')}
                    className="flex items-center space-x-3 text-sm font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest hover:translate-x-2 transition-transform"
                  >
                    <span>{t.verification.cta}</span>
                    <ArrowRight size={18} />
                  </button>
               </div>
            </div>
         </div>
      </section>

      {/* Global Reach Section */}
      <section id="reach" className="py-32 px-6 bg-slate-50 dark:bg-slate-900/50 overflow-hidden">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-end mb-20 gap-10">
            <div>
              <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase mb-6 border border-blue-200 dark:border-blue-800/50">
                <Radar size={12} className="animate-pulse" />
                <span>{t.reach.tagline}</span>
              </div>
              <h2 className="text-5xl md:text-7xl font-black text-slate-900 dark:text-white tracking-tighter italic leading-none">{t.reach.title}</h2>
            </div>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-lg max-sm max-w-sm leading-relaxed mb-2">
              {t.reach.desc}
            </p>
          </div>

          <div className="relative w-full aspect-[21/9] bg-slate-950 rounded-[4rem] border border-slate-200 dark:border-slate-800 shadow-2xl overflow-hidden group">
            {/* High Fidelity World Map Background */}
            <div className="absolute inset-0 z-0">
               <img 
                 src="assets/world-map.png" 
                 alt="World Map" 
                 className="w-full h-full object-cover opacity-60 mix-blend-screen grayscale-[20%] brightness-[1.1]"
               />
               <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-transparent to-slate-950/20"></div>
               <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_0%,rgba(2,6,23,0.8)_100%)]"></div>
            </div>

            <SimplePin x="26%" y="42%" label="New York (US Node)" />
            <SimplePin x="49%" y="38%" label="London Hub" />
            <SimplePin x="50.5%" y="60%" label="Lomé (EMEA Hub)" />
            <SimplePin x="52%" y="59.5%" label="Lagos Node" />
            <SimplePin x="49%" y="61%" label="Abidjan" />
            <SimplePin x="46%" y="56%" label="Dakar" />
            <SimplePin x="58%" y="85%" label="Johannesburg" />
            <SimplePin x="56%" y="80%" label="Gaborone" />
            <SimplePin x="61%" y="64%" label="Addis Ababa" />
            <SimplePin x="79%" y="62%" label="Bangkok Ops" />
            <SimplePin x="94%" y="88%" label="Auckland Hub" />

            <svg className="absolute inset-0 w-full h-full pointer-events-none opacity-40 z-10" viewBox="0 0 1000 500">
               <path d="M 260 210 Q 380 150 490 190" fill="none" stroke="url(#lineGradient)" strokeWidth="0.5" strokeDasharray="4,4" />
               <path d="M 490 190 Q 500 250 505 300" fill="none" stroke="url(#lineGradient)" strokeWidth="0.5" strokeDasharray="4,4" />
               <path d="M 505 300 Q 650 200 790 310" fill="none" stroke="url(#lineGradient)" strokeWidth="0.5" strokeDasharray="4,4" />
               <path d="M 790 310 Q 880 400 940 440" fill="none" stroke="url(#lineGradient)" strokeWidth="0.5" strokeDasharray="4,4" />
               <defs>
                 <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                   <stop offset="0%" stopColor="#3b82f6" />
                   <stop offset="100%" stopColor="#10b981" />
                 </linearGradient>
               </defs>
            </svg>
          </div>
        </div>
      </section>

      {/* Integrated Contact Section */}
      <section id="contact" className="py-32 px-6 bg-white dark:bg-slate-950">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-20">
            <div className="lg:col-span-7">
              {submitted ? (
                <div className="bg-slate-50 dark:bg-slate-900 p-16 rounded-[4rem] text-center shadow-2xl border border-slate-100 dark:border-slate-800 animate-in zoom-in-95 duration-500 h-full flex flex-col justify-center">
                  <div className="w-24 h-24 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-[2.5rem] flex items-center justify-center mx-auto mb-10 shadow-xl shadow-emerald-100/50">
                    <CheckCircle2 size={48} />
                  </div>
                  <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter mb-6">{t.contact.successTitle}</h2>
                  <p className="text-slate-500 dark:text-slate-400 font-medium text-xl leading-relaxed mb-12 max-w-sm mx-auto">
                    {t.contact.successDesc}
                  </p>
                  <button onClick={() => setSubmitted(false)} className="bg-slate-900 dark:bg-blue-600 text-white px-12 py-5 rounded-2xl font-black uppercase text-xs tracking-widest hover:bg-slate-800 dark:hover:bg-blue-700 transition-all shadow-xl active:scale-95 mx-auto">
                    {t.contact.successCta}
                  </button>
                </div>
              ) : (
                <div className="bg-white dark:bg-slate-900 p-12 md:p-16 rounded-[4rem] shadow-2xl border border-slate-100 dark:border-slate-800">
                  <div className="mb-12">
                     <div className="inline-flex items-center space-x-2 px-3 py-1 bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 rounded-full text-[10px] font-black uppercase mb-6 border border-blue-100 dark:border-blue-800/50">
                       <Headphones size={12} />
                       <span>{t.contact.tagline}</span>
                     </div>
                    <h2 className="text-5xl font-black text-slate-900 dark:text-white tracking-tighter mb-6 leading-none italic">{t.contact.title}</h2>
                    <p className="text-slate-500 dark:text-slate-400 font-medium text-lg">{t.contact.desc}</p>
                  </div>

                  <form onSubmit={handleContactSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <ContactInput label={t.contact.name} placeholder="Julian Casablancas" name="full_name" required />
                      <ContactInput label={t.contact.email} placeholder="julian@bank.com" type="email" name="email" required />
                    </div>
                    <div className="space-y-3">
                      <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] ml-2">{t.contact.message}</label>
                      <textarea 
                        rows={4}
                        name="message"
                        required
                        className="w-full px-8 py-5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[2rem] text-sm font-medium focus:ring-4 focus:ring-blue-500/10 outline-none transition-all dark:text-white resize-none"
                        placeholder={t.contact.messagePlaceholder}
                      ></textarea>
                    </div>
                    {error && <p className="text-rose-500 text-[10px] font-black uppercase tracking-widest text-center">{error}</p>}
                    <button 
                      disabled={loading}
                      className="w-full py-6 bg-blue-600 text-white rounded-3xl font-black uppercase text-xs tracking-[0.3em] hover:bg-blue-700 transition-all shadow-2xl shadow-blue-500/30 active:scale-95 disabled:opacity-50 flex items-center justify-center space-x-3"
                    >
                      {loading ? <div className="w-6 h-6 border-3 border-white/30 border-t-white rounded-full animate-spin" /> : <><Lock size={18} /><span>{t.contact.cta}</span></>}
                    </button>
                    <input type="hidden" name="_captcha" value="false" />
                    <input type="hidden" name="_subject" value="Minerva RegTech: New Contact Lead" />
                  </form>
                </div>
              )}
            </div>

            <div className="lg:col-span-5 space-y-12">
               <div>
                  <h3 className="text-xs font-black text-blue-600 dark:text-blue-400 uppercase tracking-[0.4em] mb-10 flex items-center">
                     <Globe size={16} className="mr-3" /> {t.contact.hq}
                  </h3>
                  <div className="space-y-6">
                     <SimpleOfficeCard city="Auckland" region="Oceania Hub"/>
                     <SimpleOfficeCard city="Bangkok" region="APAC Operations"/>
                     <SimpleOfficeCard city="Lomé" region="EMEA Compliance"/>
                  </div>
               </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-950 text-slate-900 dark:text-white pt-24 pb-12 px-6 border-t border-slate-100 dark:border-slate-900">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-16 mb-20">
            <div className="lg:col-span-2">
              <div className="flex items-center space-x-3 mb-8 cursor-pointer" onClick={() => window.scrollTo({top: 0, behavior: 'smooth'})}>
                <img src="assets/logo.png" alt="Minerva Logo" className="w-10 h-10 object-contain" />
                <span className="text-xl font-black uppercase italic tracking-tighter">Minerva Regtech </span>
              </div>
              <p className="text-slate-500 dark:text-slate-400 font-medium max-w-sm mb-10 leading-relaxed text-lg">
                {t.footer.desc}
              </p>
            </div>
            <div>
              <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-10">{t.footer.resources}</h4>
              <ul className="space-y-4 text-xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">
                <li><button onClick={() => setAuthState('about')} className="hover:text-blue-600 transition-colors">{t.footer.mission}</button></li>
                <li><button onClick={() => setAuthState('use-cases')} className="hover:text-blue-600 transition-colors">{t.footer.useCases}</button></li>
                <li><button onClick={() => scrollToSection('contact')} className="hover:text-blue-600 transition-colors">{t.footer.support}</button></li>
                <li><button onClick={() => setAuthState('demo')} className="hover:text-blue-600 transition-colors">{t.footer.register}</button></li>
              </ul>
            </div>
            <div>
               <h4 className="text-[10px] font-black uppercase tracking-[0.3em] text-slate-400 mb-10">{t.footer.connect}</h4>
               <div className="flex space-x-4">
                  <div className="w-10 h-10 bg-slate-50 dark:bg-slate-900 rounded-xl flex items-center justify-center border border-slate-100 dark:border-slate-800 hover:text-blue-600 transition-all cursor-pointer"><Globe size={18} /></div>
                  <div className="w-10 h-10 bg-slate-50 dark:bg-slate-900 rounded-xl flex items-center justify-center border border-slate-100 dark:border-slate-800 hover:text-blue-600 transition-all cursor-pointer"><Mail size={18} /></div>
                  <div className="w-10 h-10 bg-slate-50 dark:bg-slate-900 rounded-xl flex items-center justify-center border border-slate-100 dark:border-slate-800 hover:text-blue-600 transition-all cursor-pointer"><Phone size={18} /></div>
               </div>
            </div>
          </div>
          <div className="pt-8 border-t border-slate-100 dark:border-slate-900 flex flex-col md:flex-row justify-between items-center gap-6 text-[10px] font-black text-slate-400 uppercase tracking-widest">
            <p>{t.footer.rights}</p>
            <div className="flex items-center space-x-8">
               <a href="#" className="hover:text-slate-900 dark:hover:text-white transition-colors">{t.footer.privacy}</a>
            
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

// --- SUBCOMPONENTS ---

const PortfolioSection: React.FC<{ language: Language }> = ({ language }) => {
  const t = translations[language];
  const clients = [
    { name: "BOAD", logo: "/assets/portfolio/boad.png" },
    { name: "IB Bank", logo: "/assets/portfolio/ib-bank.png" },
    { name: "Bank of Zambia", logo: "/assets/portfolio/bank-of-zambia.png" },
    { name: "Ecobank", logo: "/assets/portfolio/ecobank.png" },
    { name: "BRVM", logo: "/assets/portfolio/brvm.png" },
    { name: "Praemunio", logo: "/assets/portfolio/praemunio.png" },
    { name: "Shareforce", logo: "/assets/portfolio/shareforce.png" },
  ];

  return (
    <section className="py-16 bg-white dark:bg-slate-950 border-y border-slate-100 dark:border-slate-900 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-10">
        <h2 className="text-[10px] font-black uppercase tracking-[0.4em] text-slate-400 text-center">{t.portfolio.title}</h2>
      </div>
      <div className="relative flex overflow-x-hidden">
        <div className="flex animate-marquee whitespace-nowrap py-4">
          {[...clients, ...clients, ...clients, ...clients].map((client, i) => (
            <div key={i} className="flex items-center space-x-6 mx-12 group cursor-pointer opacity-40 hover:opacity-100 transition-opacity">
              <div className="w-32 h-16 flex items-center justify-center p-2 bg-slate-50 dark:bg-slate-900 rounded-xl border border-transparent group-hover:border-blue-500/20 transition-all">
                <img 
                  src={client.logo} 
                  alt={`${client.name} Logo`} 
                  className="max-w-full max-h-full object-contain filter grayscale dark:invert dark:brightness-200 group-hover:grayscale-0 transition-all"
                  referrerPolicy="no-referrer"
                />
              </div>
              <span className="text-sm font-black uppercase tracking-widest text-slate-400 group-hover:text-slate-900 dark:group-hover:text-white transition-colors italic">
                {client.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

const RiskReason: React.FC<{ icon: React.ReactNode, title: string, desc: string }> = ({ icon, title, desc }) => (
  <div className="p-8 bg-slate-50 dark:bg-slate-900/50 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 hover:bg-white dark:hover:bg-slate-900 hover:border-blue-400 transition-all group">
    <div className="p-3 bg-white dark:bg-slate-800 rounded-2xl w-fit mb-6 shadow-sm group-hover:bg-blue-600 group-hover:text-white transition-colors">{icon}</div>
    <h4 className="text-sm font-black uppercase tracking-widest mb-3 leading-tight text-slate-900 dark:text-white">{title}</h4>
    <p className="text-xs text-slate-500 dark:text-slate-400 leading-relaxed">{desc}</p>
  </div>
);

const ContactInput: React.FC<{ label: string, placeholder: string, type?: string, required?: boolean, name?: string }> = ({ label, placeholder, type = 'text', required, name }) => (
  <div className="space-y-3">
    <label className="text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-[0.3em] ml-2">{label}</label>
    <input 
      type={type} 
      name={name}
      required={required}
      className="w-full px-8 py-5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-[2rem] text-sm font-bold focus:ring-4 focus:ring-blue-500/10 outline-none transition-all dark:text-white placeholder:text-slate-300 dark:placeholder:text-slate-600" 
      placeholder={placeholder} 
    />
  </div>
);

const SimpleOfficeCard: React.FC<{ city: string, region: string, phone?: string }> = ({ city, region, phone }) => (
  <div className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border border-slate-100 dark:border-slate-800 shadow-sm group hover:border-blue-500/30 transition-all">
    <p className="text-[10px] font-black text-blue-600 uppercase tracking-widest mb-1">{region}</p>
    <h4 className="text-xl font-black text-slate-900 dark:text-white mb-2 uppercase italic">{city}</h4>
    {phone && <p className="text-[10px] font-mono text-slate-400 font-bold">{phone}</p>}
  </div>
);

const VerificationRow: React.FC<{ icon: React.ReactNode, title: string, desc: string, status: string }> = ({ icon, title, desc, status }) => (
   <div className="flex items-center justify-between group">
      <div className="flex items-center space-x-6">
         <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">
            {icon}
         </div>
         <div>
            <p className="text-sm font-black text-slate-900 dark:text-white leading-none mb-1 uppercase tracking-tight">{title}</p>
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">{desc}</p>
         </div>
      </div>
      <div className="flex flex-col items-end">
         <span className="text-[9px] font-black text-emerald-500 uppercase tracking-widest">{status}</span>
         <div className="flex space-x-0.5 mt-1">
            {[1,2,3,4,5].map(i => <div key={i} className="w-1.5 h-1 bg-emerald-500/30 rounded-full"></div>)}
         </div>
      </div>
   </div>
);

const Globe3D: React.FC = () => (
  <div className="relative w-80 h-80 sm:w-96 sm:h-96 lg:w-[520px] lg:h-[520px] flex items-center justify-center animate-float-slow">
    <div className="absolute inset-0 bg-blue-600/10 rounded-full blur-[120px]"></div>
    <div className="absolute inset-[-20px] border border-blue-500/20 rounded-full animate-spin-slow"></div>
    <div className="relative w-full h-full rounded-full overflow-hidden shadow-[inset_-40px_-40px_80px_rgba(0,0,0,0.9),inset_30px_30px_80px_rgba(37,99,235,0.3)] border border-blue-500/30 bg-slate-900">
      <div className="absolute inset-0 z-20 pointer-events-none opacity-40">
        <svg viewBox="0 0 100 100" className="w-full h-full stroke-blue-500/40 fill-none">
          <ellipse cx="50" cy="50" rx="49.5" ry="8" /><ellipse cx="50" cy="50" rx="49.5" ry="20" /><ellipse cx="50" cy="50" rx="49.5" ry="35" /><ellipse cx="50" cy="50" rx="49.5" ry="50" /><ellipse cx="50" cy="50" rx="8" ry="49.5" /><ellipse cx="50" cy="50" rx="20" ry="49.5" /><ellipse cx="50" cy="50" rx="35" ry="49.5" />
        </svg>
      </div>
      <div className="absolute inset-0 animate-rotate-globe flex w-[200%]">
        <div className="w-1/2 h-full relative"><MapTexture /></div>
        <div className="w-1/2 h-full relative"><MapTexture /></div>
      </div>
      <div className="absolute top-[-25%] left-[-25%] w-full h-full bg-gradient-to-br from-blue-400/20 to-transparent rounded-full z-10 blur-3xl"></div>
    </div>
    <div className="absolute top-[15%] left-[25%] w-6 h-6 bg-white rounded-full blur-[12px] opacity-30"></div>
  </div>
);

const MapTexture: React.FC = () => (
  <svg viewBox="0 0 500 250" className="w-full h-full fill-blue-500/30 scale-110">
    <circle cx="100" cy="60" r="12" /><circle cx="140" cy="90" r="18" /><circle cx="80" cy="110" r="14" /><circle cx="260" cy="130" r="28" /><circle cx="300" cy="150" r="18" /><circle cx="240" cy="160" r="22" /><circle cx="420" cy="70" r="18" /><circle cx="440" cy="50" r="12" /><circle cx="390" cy="100" r="22" /><circle cx="160" cy="190" r="18" /><circle cx="340" cy="210" r="14" />
  </svg>
);

const SimplePin: React.FC<{ x: string, y: string, label: string }> = ({ x, y, label }) => (
  <div className="absolute flex flex-col items-center group z-20 transition-transform hover:scale-110 cursor-pointer" style={{ left: x, top: y, transform: 'translate(-50%, -50%)' }}>
    <div className="relative">
      <div className="absolute -inset-3 bg-blue-500/20 rounded-full animate-ping"></div>
      <div className="w-3 h-3 bg-blue-500 rounded-full border-2 border-white shadow-[0_0_15px_rgba(59,130,246,0.9)]"></div>
    </div>
    <div className="mt-2 opacity-0 group-hover:opacity-100 transition-all duration-300 absolute top-full">
      <span className="text-[9px] font-black uppercase tracking-[0.2em] text-white bg-slate-900/90 backdrop-blur-xl px-2.5 py-1 rounded-lg border border-white/10 whitespace-nowrap shadow-2xl">
        {label}
      </span>
    </div>
  </div>
);

const ServiceCard: React.FC<{ icon: React.ReactNode, title: string, desc: string, exploreText: string }> = ({ icon, title, desc, exploreText }) => (
  <div className="bg-white dark:bg-slate-900 p-10 rounded-[3rem] border border-slate-100 dark:border-slate-800 shadow-sm hover:shadow-2xl hover:-translate-y-2 transition-all group flex flex-col">
    <div className="p-4 bg-slate-50 dark:bg-slate-800 rounded-2xl w-fit mb-8 group-hover:bg-blue-600 group-hover:text-white transition-all shadow-inner">{icon}</div>
    <h3 className="text-xl font-black text-slate-900 dark:text-white mb-4 uppercase tracking-tighter leading-tight">{title}</h3>
    <p className="text-sm text-slate-500 dark:text-slate-400 font-medium leading-relaxed mb-8 flex-1">{desc}</p>
    <button className="flex items-center space-x-2 text-[10px] font-black uppercase text-blue-600 dark:text-blue-400 group-hover:translate-x-1 transition-transform">
      <span>{exploreText}</span><ArrowRight size={14} />
    </button>
  </div>
);
